<template>
  <form @submit.prevent="handleSubmit" class="group-form" novalidate>
    <FormErrorSummary :errors="errorSummary" class="mb-6" />

    <div class="form-group">
      <label for="group-name" class="form-label">
        Group Name <span class="required" aria-label="required">*</span>
      </label>
      <input
        id="group-name"
        v-model="formData.name"
        type="text"
        class="form-input"
        :class="{ 'error': errors.name }"
        placeholder="Enter a name for your group"
        :disabled="isLoading"
        @blur="validateField('name')"
      />
      <p v-if="errors.name" class="error-message">{{ errors.name }}</p>
    </div>

    <div class="form-group">
      <label for="group-description" class="form-label">
        Description
      </label>
      <textarea
        id="group-description"
        v-model="formData.description"
        class="form-textarea"
        :class="{ 'error': errors.description }"
        rows="4"
        placeholder="Describe the purpose of this group (optional)"
        :disabled="isLoading"
        @blur="validateField('description')"
      />
      <p v-if="errors.description" class="error-message">{{ errors.description }}</p>
      <p class="form-hint">{{ formData.description?.length || 0 }}/500 characters</p>
    </div>

    <div v-if="!isEditMode" class="form-group">
      <label class="checkbox-label">
        <input
          v-model="formData.is_personal"
          type="checkbox"
          class="form-checkbox"
          :disabled="isLoading"
        />
        <span class="checkbox-text">
          <span class="checkbox-title">Personal Group</span>
          <span class="checkbox-description">
            Personal groups are private and only visible to you
          </span>
        </span>
      </label>
    </div>

    <div v-if="serverError" class="server-error">
      <i class="icon-alert"></i>
      {{ serverError }}
    </div>

    <div class="form-actions">
      <button 
        type="button" 
        @click="$emit('cancel')" 
        class="btn btn-secondary"
        :disabled="isLoading"
      >
        Cancel
      </button>
      <button
        type="submit"
        class="btn btn-primary"
        :disabled="isLoading"
      >
        <span v-if="isLoading" class="spinner"></span>
        {{ submitButtonText }}
      </button>
    </div>
  </form>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import type { Group, CreateGroupData, UpdateGroupData } from '../../types/group';
import FormErrorSummary from '../FormErrorSummary.vue'
import { vm } from '../../utils/validationMessages'

const props = defineProps<{
  group?: Group;
  isLoading?: boolean;
  serverError?: string | null;
}>();

const emit = defineEmits<{
  submit: [data: CreateGroupData | UpdateGroupData];
  cancel: [];
}>();

const isEditMode = computed(() => !!props.group);

const formData = ref<CreateGroupData>({
  name: '',
  description: '',
  is_personal: false
});

const errors = ref({
  name: '',
  description: ''
})

const submitAttempted = ref(false)

const errorSummary = computed(() => {
  if (!submitAttempted.value) return []
  const items = []
  if (errors.value.name) items.push({ label: 'Group Name', message: errors.value.name })
  if (errors.value.description) items.push({ label: 'Description', message: errors.value.description })
  return items
})

const isValid = computed(() => {
  return formData.value.name.trim().length > 0 && 
         !errors.value.name && 
         !errors.value.description;
});

const submitButtonText = computed(() => {
  if (props.isLoading) {
    return isEditMode.value ? 'Saving...' : 'Creating...';
  }
  return isEditMode.value ? 'Save Changes' : 'Create Group';
});

onMounted(() => {
  if (props.group) {
    formData.value = {
      name: props.group.name,
      description: props.group.description,
      is_personal: props.group.is_personal
    };
  }
});

const validateField = (field: 'name' | 'description') => {
  errors.value[field] = '';
  
  switch (field) {
    case 'name':
      if (!formData.value.name.trim()) {
        errors.value.name = vm.groupName.required
      } else if (formData.value.name.length < 3) {
        errors.value.name = vm.groupName.minLength
      } else if (formData.value.name.length > 100) {
        errors.value.name = vm.groupName.maxLength
      }
      break

    case 'description':
      if (formData.value.description && formData.value.description.length > 500) {
        errors.value.description = vm.groupDescription.maxLength
      }
      break
  }
};

const validateForm = () => {
  validateField('name');
  validateField('description');
  return isValid.value;
};

const handleSubmit = () => {
  submitAttempted.value = true
  if (!validateForm()) {
    return;
  }
  
  const data = isEditMode.value 
    ? {
        name: formData.value.name,
        description: formData.value.description
      } as UpdateGroupData
    : formData.value;
    
  emit('submit', data);
};

// Clear server error when form data changes
watch(formData, () => {
  if (props.serverError) {
    // Parent should clear serverError
  }
}, { deep: true });
</script>

<style scoped>
@import '../../assets/main.css';

.group-form {
  max-width: 600px;
  margin: 0 auto;
}

.form-group {
  margin-bottom: var(--spacing-6);
}

.required {
  color: #ef4444;
}

.form-input,
.form-textarea {
  width: 100%;
  padding: var(--spacing-3) var(--spacing-4);
  border: 1px solid rgb(229 231 235);
  border-radius: var(--radius-lg);
  font-size: var(--font-size-base);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  background: white;
}

.form-input:hover,
.form-textarea:hover {
  border-color: rgb(156 163 175);
  box-shadow: 0 0 0 1px rgba(156, 163, 175, 0.1);
}

.form-input:focus,
.form-textarea:focus {
  outline: 2px solid var(--color-primary-500);
  border-color: transparent;
  box-shadow: 0 0 0 4px rgba(76, 175, 80, 0.1);
}

.form-input.error,
.form-textarea.error {
  border-color: #ef4444;
  box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.1);
}

.form-input.error:focus,
.form-textarea.error:focus {
  border-color: #ef4444;
  box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.2);
}

.form-input:disabled,
.form-textarea:disabled {
  background: #f9fafb;
  cursor: not-allowed;
  opacity: 0.6;
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
}

.form-hint {
  margin-top: var(--spacing-1);
  font-size: var(--font-size-sm);
  color: #9ca3af;
}

.error-message {
  margin-top: var(--spacing-1);
  font-size: var(--font-size-sm);
  color: #ef4444;
}

.checkbox-label {
  display: flex;
  align-items: flex-start;
  cursor: pointer;
}

.form-checkbox {
  margin-top: var(--spacing-0_5);
  margin-right: var(--spacing-3);
  width: var(--spacing-5);
  height: var(--spacing-5);
  border-radius: var(--radius-sm);
  cursor: pointer;
  accent-color: var(--color-primary-500);
}

.checkbox-text {
  flex: 1;
}

.checkbox-title {
  display: block;
  font-weight: var(--font-weight-medium);
  color: rgb(55 65 81);
  margin-bottom: var(--spacing-1);
}

.checkbox-description {
  display: block;
  font-size: var(--font-size-sm);
  color: rgb(107 114 128);
  line-height: 1.5;
}

.server-error {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  padding: var(--spacing-3) var(--spacing-4);
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: var(--radius-lg);
  color: #991b1b;
  margin-bottom: var(--spacing-6);
}

.icon-alert::before {
  content: '⚠️';
  font-size: var(--font-size-xl);
}

.form-actions {
  display: flex;
  gap: var(--spacing-3);
  justify-content: flex-end;
  padding-top: var(--spacing-6);
  border-top: 1px solid rgb(229 231 235);
}

.spinner {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
